import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { createSlug } from "../Utils/HelperFunctions";
import { get } from "../Services/api.service";
import { Paths } from "../Services/EndPoints";
import { useNavigate } from "react-router-dom";
import { ImageKitThumbNail } from "../Pages/CommonPages/ImageKit";
const ICONS = {
  1: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3l4 4-4 14-4-14z" />
      <path d="M8 7h8" />
    </svg>
  ),
  2: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M7 4h10l-2 4H9z" />
      <path d="M9 8c-2 2-2 6 0 8s4 2 6 0 2-6 0-8" />
    </svg>
  ),
  3: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="3" width="6" height="6" rx="1" />
      <path d="M9 9l-1 12h8l-1-12" />
      <path d="M11 13h2" />
    </svg>
  ),
};

export default function PinboardShelf({ boards }) {
  const trackRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [prevDisabled, setPrevDisabled] = useState(true);
  const [nextDisabled, setNextDisabled] = useState(false);
  const navigate = useNavigate();
  // When `boards` is supplied (e.g. from the consolidated get_home_page payload),
  // use it and skip the standalone fetch; otherwise fetch on its own.
  const { data } = useSWR(boards ? null : Paths.get_pinboard_data, get, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
    dedupingInterval: 60000,
  });
  const shelfBoards = normalizeBoards(boards ?? data?.data?.boards);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handleScroll = () => {
      const children = Array.from(track.children);
      const trackCenter = track.scrollLeft + track.offsetWidth / 2;

      let closestIdx = 0;
      let minDistance = Infinity;

      children.forEach((child, index) => {
        const childCenter = child.offsetLeft + child.offsetWidth / 2;

        const distance = Math.abs(trackCenter - childCenter);

        if (distance < minDistance) {
          minDistance = distance;
          closestIdx = index;
        }
      });

      setActiveIdx(closestIdx);
      // Arrow enable/disable is driven by the REAL scroll position (not
      // activeIdx, which scroll-snap clamps before the track reaches its ends).
      setPrevDisabled(track.scrollLeft <= 0);
      setNextDisabled(
        track.scrollLeft >= track.scrollWidth - track.clientWidth - 1,
      );
    };

    track.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => track.removeEventListener("scroll", handleScroll);
  }, [shelfBoards.length]);

  const goTo = (i) => {
    const track = trackRef.current;
    if (!track) return;

    const child = track.children[i];

    if (!child) return;

    const scrollTo =
      child.offsetLeft - track.offsetWidth / 2 + child.offsetWidth / 2;

    track.scrollTo({
      left: scrollTo,
      behavior: "smooth",
    });
  };

  // Arrows drive the scroll container directly (one board width + gap per
  // click). This reaches the true ends even when scroll-snap clamps activeIdx.
  const scrollByStep = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const first = track.children[0];
    const step = first ? first.offsetWidth + 10 : track.clientWidth * 0.9;
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const handlePinClick = (pin) => {
    if (!pin?.productId) return;

    const productSlug = createSlug(pin?.name || "product");
    const skuQuery = pin?.skuId ? `?skuId=${pin.skuId}` : "";

    navigate(`/product/${productSlug}/${pin.productId}${skuQuery}`);
  };

  if (shelfBoards.length === 0) {
    return null;
  }

  return (
    <section className="mb-[18px] font-sans max-w-[1440px] mx-auto">
      {/* TRACK */}
      <div
        ref={trackRef}
        className="flex gap-[10px] overflow-x-auto px-[16px] pt-[4px] pb-[8px] snap-x snap-mandatory no-scrollbar xl:px-[24px]"
      >
        {shelfBoards.map((board) => (
          <div
            key={board.id}
            className={`snap-center shrink-0 w-[calc(100%-32px)] max-w-[420px] mx-auto rounded-[18px] p-[12px] pb-[10px] shadow-[0_2px_12px_rgba(31,33,46,0.07)] border border-[rgba(31,33,46,0.04)] ${
              BG_MAP[board.id] || DEFAULT_BOARD_BG
            }`}
          >
            {/* HEADER */}
            <div className="flex items-center gap-[8px] pb-[10px]">
              <div
                className={`w-[22px] h-[22px] rounded-[6px] grid place-items-center text-white ${
                  ICON_BG[board.id] || DEFAULT_ICON_BG
                }`}
              >
                <div className="w-[13px] h-[13px]">
                  {ICONS[board.id] || DEFAULT_ICON}
                </div>
              </div>
              <span className="text-[14px] font-[500] tracking-[-0.01em] text-[#1F212E] whitespace-nowrap">
                Today's {board.name} Picks
              </span>
            </div>

            {/* GRID */}
            <div className="grid grid-cols-4 gap-[8px] xl:gap-[10px]">
              {board.pins.map((pin, index) => (
                <button
                  key={
                    pin.id ||
                    pin.skuId ||
                    pin.productId ||
                    `${board.id}-${index}`
                  }
                  type="button"
                  className="flex flex-col gap-[5px] text-left"
                  onClick={() => handlePinClick(pin)}
                  aria-label={pin?.name ? `Open ${pin.name}` : "Open product"}
                >
                  <div
                    className={`relative w-full aspect-square max-h-[110px] sm:max-h-none rounded-[12px] overflow-hidden shadow-[0_1px_3px_rgba(31,33,46,0.08)] ${
                      SURFACE_MAP[pin.id]
                    }`}
                  >
                    {/* <OptimizedImage
                      src={pin.imageUrl}
                      alt={pin.name || ""}
                      width={220}
                      height={220}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      priority={true}
                    /> */}
                    <ImageKitThumbNail Thumbnail={pin.imageUrl} />

                    {pin.isFresh && (
                      <span className="absolute top-[6px] right-[6px] bg-[#1F212E] text-white text-[8px] font-[400] px-[5px] py-[2px] rounded-[4px] tracking-[0.04em]">
                        NEW
                      </span>
                    )}
                  </div>

                  <span className="text-[10.5px] font-[400] text-[#1F212E] text-center leading-[1.2] tracking-[-0.005em] line-clamp-2 min-h-[26px]">
                    {pin.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* NAV — dots on MOBILE (all boards aren't visible, so position matters),
          arrows on DESKTOP (all ~3 boards are visible at once, so an "active"
          dot is misleading — the prev/next arrows are the clear control). Each
          side is swapped by breakpoint: dots lg:hidden, arrows hidden lg:grid. */}
      {shelfBoards.length > 1 && (
        <div className="flex items-center justify-center gap-[14px] pt-[4px] pb-[4px] lg:pt-[12px] lg:pb-[6px]">
          <button
            type="button"
            onClick={() => scrollByStep(-1)}
            disabled={prevDisabled}
            aria-label="Previous boards"
            className="hidden lg:grid place-items-center w-[38px] h-[38px] rounded-full bg-white text-[#1F212E] border border-[rgba(31,33,46,0.12)] shadow-[0_2px_8px_rgba(31,33,46,0.10)] transition-colors hover:bg-[#FF512A] hover:text-white hover:border-[#FF512A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF512A] focus-visible:ring-offset-2 disabled:opacity-35 disabled:pointer-events-none"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-[18px] h-[18px]"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          {/* Each dot sits in a 24x24 tap target (9px padding round a 6px dot; 36x24 when active).
              Targets touch but don't overlap, so dots are now 18px apart instead of 6px. */}
          <div className="flex items-center lg:hidden">
            {shelfBoards.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to board ${i + 1}`}
                aria-current={i === activeIdx ? "true" : undefined}
                className="p-[9px] flex items-center justify-center"
              >
                <span
                  aria-hidden="true"
                  className={`block transition-all duration-200 ${
                    i === activeIdx
                      ? "bg-[#1F212E] w-[18px] h-[6px] rounded-full"
                      : "bg-[#E8EAF0] w-[6px] h-[6px] rounded-full"
                  }`}
                />
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => scrollByStep(1)}
            disabled={nextDisabled}
            aria-label="Next boards"
            className="hidden lg:grid place-items-center w-[38px] h-[38px] rounded-full bg-white text-[#1F212E] border border-[rgba(31,33,46,0.12)] shadow-[0_2px_8px_rgba(31,33,46,0.10)] transition-colors hover:bg-[#FF512A] hover:text-white hover:border-[#FF512A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF512A] focus-visible:ring-offset-2 disabled:opacity-35 disabled:pointer-events-none"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-[18px] h-[18px]"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      )}
    </section>
  );
}

const BG_MAP = {
  1: "bg-[linear-gradient(160deg,#FEEFEB_0%,#FFFFFF_55%)]",
  2: "bg-[linear-gradient(160deg,#E9EDFB_0%,#FFFFFF_55%)]",
  3: "bg-[linear-gradient(160deg,#FCE0E9_0%,#FFFFFF_55%)]",
};

const ICON_BG = {
  1: "bg-[#FF512A]",
  2: "bg-[#8B5CD2]",
  3: "bg-[#E5557A]",
};

const SURFACE_MAP = {
  1: "bg-[#FEEFEB]",
  4: "bg-[#FFEBB8]",
  5: "bg-[#FBD9D2]",
  6: "bg-[#C2DFDA]",
  7: "bg-[#F4C99B]",
  8: "bg-[#B8EAFF]",
  2: "bg-[#E9EDFB]",
  3: "bg-[#FCE0E9]",
};

// Fallbacks for boards whose id isn't one of the known category types (1/2/3),
// so an unexpected board still renders a gradient card + an icon (never blank).
const DEFAULT_BOARD_BG = "bg-[linear-gradient(160deg,#F4F6F8_0%,#FFFFFF_55%)]";
const DEFAULT_ICON_BG = "bg-[#1F212E]";
const DEFAULT_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="7" />
    <path d="M12 8v4l2 2" />
  </svg>
);

const normalizeBoards = (items) => {
  if (!Array.isArray(items)) return [];

  return items
    .filter(Boolean)
    .map((board, boardIndex) => ({
      id: board?.id ?? `board-${boardIndex}`,
      name: board?.name || "Untitled Board",
      pins: Array.isArray(board?.pins)
        ? board.pins.filter(Boolean).slice(0, 8).map(normalizePin)
        : [],
    }))
    .filter((board) => board.pins.length > 0);
};

const normalizePin = (pin, pinIndex) => ({
  ...pin,
  id: pin?.id ?? `pin-${pinIndex}`,
  name: pin?.name || "Untitled Product",
  imageUrl: pin?.imageUrl || "",
});
